/**
 * Findings, the punch list and the two-party response loop.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * THE PRIVACY THIS MODULE PRESERVES BY CONSTRUCTION
 *   The server masks three things on a counterparty's projection: the escalation
 *   suggestion (`promotion_suggested` arrives as null), the assessor's identity
 *   (`observed_by_name` arrives as "Operator Quality Representative") and the internal
 *   deliberations. This module exposes those fields exactly as they arrive and marks
 *   the suggestion `null` where the server did, so a screen can only ever render the
 *   answer. It has no way to reconstruct what the server removed, which is the point.
 *
 *   The register is server-paginated: `limit` and `offset` ride on every call, the
 *   total comes back beside the page, and no screen ever sees a row the server did not
 *   send for this principal.
 */

import { apiJson, apiUpload, bool, listBody, num, optStr, query, str } from "./client";

export interface FindingSummary {
  id: string;
  humanId: string;
  findingClass: string;
  findingStatus: string;
  verificationId: string;
  verificationLineId: string;
  contractorPartyId: string;
  observedByName: string | null;
  observedAt: string;
  description: string;
  subjectItemRef: string | null;
  discoveredAfterRelease: boolean;
  punchListId: string | null;
  punchListName: string | null;
  /** Null exactly when the server masked it for this principal. */
  promotionSuggested: string | null;
  actionRequestId: string | null;
  targetCompletionDate: string | null;
  daysOpen: number | null;
  closureReference: string | null;
  closedAt: string | null;
  punchRejectionReason: string | null;
  /** The lessons-learned flag (Ruling 18): NONE, CANDIDATE or CAPTURED. */
  lessonLearnedStatus: string;
  /** Wave R0 Break 7: who declared the agreed correction implemented, when, and how. */
  implementedBy: string | null;
  implementedAt: string | null;
  implementationNote: string | null;
}

export interface FindingItem {
  id: string;
  seq: number;
  itemText: string;
}

export interface FindingTransition {
  id: string;
  event: string;
  fromState: string;
  toState: string;
  actorDisplayName: string | null;
  comment: string | null;
  at: string;
}

export interface FindingDetail extends FindingSummary {
  items: FindingItem[];
  transitions: FindingTransition[];
  /** True when the caller is the person who marked the punch item completed (INV-2). */
  punchCompletedByMe: boolean;
  /**
   * Wave W2 (Mahmoud's ruling 2026-10-10): whether THIS caller may put the finding on a
   * punch list or mark its punch item complete, the operator's acts. The server's answer,
   * asked of the same predicate as the two endpoints.
   */
  mayActOnPunch: boolean;
  /** Whether THIS caller holds the verb to distribute the finding for response. */
  mayDistribute: boolean;
  /**
   * Wave R0: true when THIS caller declared the implementation. The client holds the
   * identity provider's subject, not the person id, so the server answers the comparison;
   * the screen never offers the declarer the verification.
   */
  implementedByMe: boolean;
  /**
   * The inspection day: where the finding came from, the checklist item, the inspection
   * and the source that fed it (the downstream trace). Null on a record without one.
   */
  origin: FindingOrigin | null;
}

export interface FindingOrigin {
  inspectionId: string;
  inspectionHumanId: string;
  inspectionTitle: string;
  /** RFI, OPERATOR_REQUEST or PLANNED_ACTIVITY. */
  source: string;
  activityId: string | null;
  activityHumanId: string | null;
  itemNumber: string;
  itemSection: string | null;
  itemText: string;
  checklistCode: string | null;
}

function findingOrigin(raw: unknown): FindingOrigin | null {
  if (typeof raw !== "object" || raw === null) {
    return null;
  }
  const body = raw as Record<string, unknown>;
  return {
    inspectionId: str(body.inspection_id),
    inspectionHumanId: str(body.inspection_human_id),
    inspectionTitle: str(body.inspection_title),
    source: str(body.source),
    activityId: optStr(body.activity_id),
    activityHumanId: optStr(body.activity_human_id),
    itemNumber: str(body.item_number),
    itemSection: optStr(body.item_section),
    itemText: str(body.item_text),
    checklistCode: optStr(body.checklist_code),
  };
}

export interface FindingResponse {
  id: string;
  findingId: string;
  roundNo: number;
  proposedByPartyId: string;
  proposedCorrection: string;
  proposedCorrectiveAction: string | null;
  proposedTargetDate: string;
  submittedAt: string;
  decision: string;
  decisionComment: string | null;
}

export interface PunchListSummary {
  id: string;
  humanId: string;
  name: string;
  status: string;
}

function assertRecord(body: unknown): Record<string, unknown> {
  if (typeof body !== "object" || body === null) {
    throw new Error("Expected an object body");
  }
  return body as Record<string, unknown>;
}

function summary(body: Record<string, unknown>): FindingSummary {
  return {
    id: str(body.id),
    humanId: str(body.human_id),
    findingClass: str(body.finding_class),
    findingStatus: str(body.finding_status),
    verificationId: str(body.verification_id),
    verificationLineId: str(body.verification_line_id),
    contractorPartyId: str(body.contractor_party_id),
    observedByName: optStr(body.observed_by_name),
    observedAt: str(body.observed_at),
    description: str(body.description),
    subjectItemRef: optStr(body.subject_item_ref),
    discoveredAfterRelease: bool(body.discovered_after_release),
    punchListId: optStr(body.punch_list_id),
    punchListName: optStr(body.punch_list_name),
    promotionSuggested: optStr(body.promotion_suggested),
    actionRequestId: optStr(body.action_request_id),
    targetCompletionDate: optStr(body.target_completion_date),
    daysOpen: optNum(body.days_open),
    closureReference: optStr(body.closure_reference),
    closedAt: optStr(body.closed_at),
    punchRejectionReason: optStr(body.punch_rejection_reason),
    lessonLearnedStatus: str(body.lesson_learned_status),
    implementedBy: optStr(body.implemented_by),
    implementedAt: optStr(body.implemented_at),
    implementationNote: optStr(body.implementation_note),
  };
}

function optNum(value: unknown): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  return num(value);
}

export type FindingClass = "NEGATIVE_OBSERVATION" | "COMMENT" | "POSITIVE_OBSERVATION" | "OFI";

export async function listFindings(
  token: string,
  filters: {
    projectId?: string | null;
    findingClass?: FindingClass | null;
    /**
     * One state, or (wave W1) a set of states: the server takes the parameter repeated,
     * so a queue that counts several states lands on exactly that set, paged by the server.
     */
    findingStatus?: string | readonly string[] | null;
    /** The stage/discipline stamp filters (P2): the rows carry both axes. */
    stageId?: string | null;
    disciplineId?: string | null;
    verificationId?: string | null;
    punchListId?: string | null;
    /** The lessons-learned status filter (ADR-025): NONE, CANDIDATE or CAPTURED. */
    lessonStatus?: string | null;
    limit?: number;
    offset?: number;
    signal?: AbortSignal;
  } = {},
): Promise<{ total: number; items: FindingSummary[] }> {
  const states =
    filters.findingStatus === null || filters.findingStatus === undefined
      ? []
      : typeof filters.findingStatus === "string"
        ? [filters.findingStatus]
        : filters.findingStatus;
  const base = query({
    project_id: filters.projectId,
    finding_class: filters.findingClass,
    stage_id: filters.stageId,
    discipline_id: filters.disciplineId,
    verification_id: filters.verificationId,
    punch_list_id: filters.punchListId,
    lesson_status: filters.lessonStatus,
    limit: filters.limit,
    offset: filters.offset,
  });
  const repeated = states.map((state) => `finding_status=${encodeURIComponent(state)}`).join("&");
  const path =
    repeated.length === 0
      ? `/findings${base}`
      : `/findings${base.length === 0 ? "?" : `${base}&`}${repeated}`;
  const body = await apiJson(token, path, listBody, { signal: filters.signal });
  return { total: body.total, items: body.items.map(summary) };
}

export async function getFinding(
  token: string,
  findingId: string,
  signal?: AbortSignal,
): Promise<FindingDetail> {
  return apiJson(
    token,
    `/findings/${findingId}`,
    (raw) => {
      const body = assertRecord(raw);
      const items = Array.isArray(body.items) ? body.items : [];
      const transitions = Array.isArray(body.transitions) ? body.transitions : [];
      return {
        ...summary(body),
        punchCompletedByMe: bool(body.punch_completed_by_me),
        mayActOnPunch: body.may_act_on_punch === true,
        mayDistribute: body.may_distribute === true,
        implementedByMe: body.implemented_by_me === true,
        origin: findingOrigin(body.origin),
        items: items.map((entry) => {
          const item = assertRecord(entry);
          return { id: str(item.id), seq: num(item.seq), itemText: str(item.item_text) };
        }),
        transitions: transitions.map((entry) => {
          const transition = assertRecord(entry);
          return {
            id: str(transition.id),
            event: str(transition.event),
            fromState: str(transition.from_state),
            toState: str(transition.to_state),
            actorDisplayName: optStr(transition.actor_display_name),
            comment: optStr(transition.comment),
            at: str(transition.at),
          };
        }),
      };
    },
    { signal },
  );
}

async function post(token: string, path: string, body: unknown): Promise<Record<string, unknown>> {
  return apiJson(token, path, (raw) => assertRecord(raw), { method: "POST", body });
}

/** Insert an open comment finding into a project punch list. */
export async function insertInPunchList(
  token: string,
  findingId: string,
  punchListId: string,
): Promise<void> {
  await post(token, `/findings/${findingId}/punch-list`, { punch_list_id: punchListId });
}

export async function completePunch(
  token: string,
  findingId: string,
  completionNotes: string | null,
): Promise<void> {
  await post(token, `/findings/${findingId}/punch-complete`, {
    completion_notes: completionNotes,
  });
}

export async function acceptPunch(
  token: string,
  findingId: string,
  payload: { closure_reference?: string | null; closure_comments?: string | null },
): Promise<void> {
  await post(token, `/findings/${findingId}/punch-accept`, payload);
}

/** Reject completed punch work. The server demands a non-blank reason and refuses without one. */
export async function rejectPunch(token: string, findingId: string, reason: string): Promise<void> {
  await post(token, `/findings/${findingId}/punch-reject`, { comment: reason });
}

/** Record the operator's escalation suggestion. The server masks it from counterparties. */
export async function suggestPromotion(
  token: string,
  findingId: string,
  promotionSuggested: "NONE" | "NCR" | "CAR",
): Promise<void> {
  await post(token, `/findings/${findingId}/suggest-promotion`, {
    promotion_suggested: promotionSuggested,
  });
}

/** Promote the finding to a formal action. Promotion never happens by suggestion alone. */
export async function promoteFinding(
  token: string,
  findingId: string,
  payload: { action_type: string; title?: string | null; description?: string | null },
): Promise<{ actionRequestId: string | null }> {
  const body = await post(token, `/findings/${findingId}/promote`, payload);
  return { actionRequestId: optStr(body.action_request_id) };
}

/**
 * The lessons-learned setter (Ruling 18, ADR-025 decision 4): mark a CLOSED finding a
 * lesson candidate or captured. Operator side only; the server refuses everyone else
 * and the screen renders that refusal as it arrives.
 */
export async function setLessonLearned(
  token: string,
  findingId: string,
  status: "CANDIDATE" | "CAPTURED",
  note: string | null,
): Promise<{ lessonLearnedStatus: string }> {
  const body = await post(token, `/findings/${findingId}/lesson`, {
    status,
    note: note,
  });
  return { lessonLearnedStatus: str(body.lesson_learned_status) };
}

/** Distribute an OPEN finding to the contractor for formal response. */
export async function distributeFinding(
  token: string,
  findingId: string,
  comment?: string | null,
): Promise<void> {
  await post(token, `/findings/${findingId}/distribute-for-response`, {
    comment: comment && comment.trim().length > 0 ? comment.trim() : null,
  });
}

function response(body: Record<string, unknown>): FindingResponse {
  return {
    id: str(body.id),
    findingId: str(body.finding_id),
    roundNo: num(body.round_no),
    proposedByPartyId: str(body.proposed_by_party_id),
    proposedCorrection: str(body.proposed_correction),
    proposedCorrectiveAction: optStr(body.proposed_corrective_action),
    proposedTargetDate: str(body.proposed_target_date),
    submittedAt: str(body.submitted_at),
    decision: str(body.decision),
    decisionComment: optStr(body.decision_comment),
  };
}

export async function listResponses(
  token: string,
  findingId: string,
  signal?: AbortSignal,
): Promise<FindingResponse[]> {
  return apiJson(
    token,
    `/findings/${findingId}/responses`,
    (raw) => {
      const body = assertRecord(raw);
      const items = Array.isArray(body.items) ? body.items : [];
      return items.map((entry) => response(assertRecord(entry)));
    },
    { signal },
  );
}

/** The contractor's proposal: correction, corrective action and a target date. */
export async function proposeResponse(
  token: string,
  findingId: string,
  payload: {
    proposed_correction: string;
    proposed_corrective_action?: string | null;
    proposed_target_date: string;
  },
): Promise<void> {
  await post(token, `/findings/${findingId}/responses`, payload);
}

/** The operator's decision on a round. A rejection demands its comment at the server. */
export async function decideResponse(
  token: string,
  findingId: string,
  responseId: string,
  decision: "AGREE" | "REJECT",
  comment: string | null,
): Promise<void> {
  await post(token, `/findings/${findingId}/responses/${responseId}/decide`, {
    decision,
    comment,
  });
}

export async function listPunchLists(
  token: string,
  options?: { status?: string; signal?: AbortSignal } | AbortSignal,
): Promise<{ total: number; items: PunchListSummary[] }> {
  const signal = options instanceof AbortSignal ? options : options?.signal;
  const status = options instanceof AbortSignal ? undefined : options?.status;
  const body = await apiJson(token, `/punch-lists${query({ limit: 100, status })}`, listBody, {
    signal,
  });
  return {
    total: body.total,
    items: body.items.map((entry) => ({
      id: str(entry.id),
      humanId: str(entry.human_id),
      name: str(entry.name),
      status: str(entry.status),
    })),
  };
}

/**
 * Wave R0 Break 7: the contractor declares the agreed correction implemented (AGREED to
 * IMPLEMENTED). The evidence files are uploaded first, each through the gated upload,
 * and their ids travel with the note; the server links them to the finding.
 */
export async function declareImplementation(
  token: string,
  findingId: string,
  payload: { note: string; files: readonly File[] },
): Promise<FindingSummary> {
  const attachmentIds: string[] = [];
  for (const file of payload.files) {
    const uploaded = await apiUpload(token, "/attachments/upload", file, (raw) => ({
      id: str(assertRecord(raw).id),
    }));
    attachmentIds.push(uploaded.id);
  }
  const body = await post(token, `/findings/${findingId}/implementation`, {
    note: payload.note,
    attachment_ids: attachmentIds,
  });
  return summary(body);
}

/**
 * Wave R0 Break 7: the operator's verdict on a declared implementation. CLOSE closes the
 * finding; REJECT returns it to AGREED and needs its reason (the server refuses without).
 */
export async function verifyFindingClosure(
  token: string,
  findingId: string,
  payload: { decision: "CLOSE" | "REJECT"; reason: string | null; closureReference: string | null },
): Promise<FindingSummary> {
  const body = await post(token, `/findings/${findingId}/verify-closure`, {
    decision: payload.decision,
    reason: payload.reason,
    closure_reference: payload.closureReference,
  });
  return summary(body);
}
